/* eslint-disable @typescript-eslint/ban-ts-comment */
import { I18nt } from 'utils';
import { AutoComplete, Button, Col, Empty, Input, Row } from 'antd';
import {
	EyeFilled,
	PlusCircleFilled,
	MinusCircleFilled,
	ExclamationCircleFilled,
} from '@ant-design/icons';
import BotiquinCard from 'components/BotiquinCard';
import styles from './ConsultaMedicamentos.module.less';
import BannerWithImage from 'components/BannerWithImage';
import { useConsultaMedicamentos } from '../ConsultaMedicamentosContext';
import { useState } from 'react';
import { format } from 'date-fns';
import Info from 'components/Info';
import cn from 'classnames';

const { Search } = Input;

export default function ResultsView() {
	const [searchText, setSearchText] = useState('');
	const {
		perPage,
		nextPage,
		resetPage,
		totalPages,
		botiquines,
		venceAhora,
		resetState,
		setShowMiReceta,
		botiquinesInPage,
		filterByDepartamento,
	} = useConsultaMedicamentos();

	const onSearch = (text: string) => {
		setSearchText(text);
		filterByDepartamento(text);
	};

	const actionButtons = () => {
		if (botiquines.length === 0) {
			return null;
		}

		if (botiquines.length <= perPage) {
			return null;
		}

		if (searchText.length) {
			return null;
		}

		if (totalPages > 1 && botiquinesInPage.length === botiquines.length) {
			return (
				<Button onClick={resetPage} type='link'>
					<MinusCircleFilled />
					{I18nt('ver_menos')}
				</Button>
			);
		}

		return (
			<Button onClick={nextPage} type='link'>
				<PlusCircleFilled />
				{I18nt('ver_mas')}
			</Button>
		);
	};

	const botiquinCards = () => {
		if (botiquinesInPage.length === 0) {
			return <Empty description={I18nt('informacion_no_encontrada')} />;
		}

		return botiquinesInPage.map((botiquin, index) => (
			<BotiquinCard key={index} botiquin={botiquin} />
		));
	};

	const options = () => {
		const departamentos = Array.from(
			new Set(botiquines.map(({ departamento }) => departamento))
		)
			.sort()
			.reverse();

		const municipios = Array.from(
			new Set(botiquines.map(({ municipio }) => municipio))
		)
			.sort()
			.reverse();

		const data = botiquines
			.sort()
			.reverse()
			.map(({ botiquin }) => ({
				label: botiquin,
				value: botiquin,
			}));

		Array.from(
			new Set([...departamentos, ...municipios].map((value) => value))
		).forEach((item) => {
			data.unshift({
				label: item,
				value: item,
			});
		});

		return data;
	};

	const getDate = () => {
		return format(new Date(), 'dd/MM/yyyy - hh:mm aa').toLowerCase();
	};

	const buttonsContainer = () => (
		<div className={styles.buttonsContainer}>
			<Button onClick={resetState} type='primary'>
				{I18nt('nueva_consulta')}
			</Button>
			<Button onClick={setShowMiReceta} type='text'>
				<EyeFilled />
				&nbsp;
				{I18nt('ver_mi_receta')}
			</Button>
		</div>
	);

	const textContainer = () => (
		<div className={styles.textContainer}>
			<p>{I18nt('puedes_encontrar_tu_medicamento')}</p>
			<p>
				<span>{I18nt('fecha_y_hora')}:</span>
				&nbsp;
				{getDate()}
			</p>
		</div>
	);

	const schedule = () => (
		<div className={styles.schedule}>
			<div>
				<div className={styles.scheduleTitle}>{I18nt('horario_general')}</div>
				<small
					className={cn(styles.scheduleSmall, styles.scheduleSmallDesktop)}
				>
					{I18nt('horario_aplica_para')}
				</small>
			</div>
			<div className={styles.scheduleText}>
				<div>{I18nt('lunes_a_viernes')}</div>
				<div>{I18nt('sabado')}</div>
			</div>
			<small className={cn(styles.scheduleSmall, styles.scheduleSmallMobile)}>
				{I18nt('horario_aplica_para')}
			</small>
		</div>
	);

	const searchBar = () => (
		<div>
			<AutoComplete
				allowClear
				filterOption={true}
				options={options()}
				style={{ width: '100%' }}
				onClear={() => onSearch('')}
				//@ts-ignore
				onSelect={(value) => onSearch(value)}
			>
				<Search
					enterButton
					onSearch={onSearch}
					placeholder={I18nt('consulta_por_departamento')}
					onChange={({ target }) => {
						if (target.value.length === 0) {
							onSearch('');
						}
					}}
				/>
			</AutoComplete>
		</div>
	);

	const mobileView = () => (
		<div className={styles.mobileView}>
			{buttonsContainer()}
			{textContainer()}
			{searchBar()}
		</div>
	);

	const desktopView = () => (
		<div className={styles.desktopView}>
			{textContainer()}
			<Row justify='space-between'>
				<Col md={12} lg={10} xl={8}>
					{searchBar()}
				</Col>
				<Col md={10} lg={8}>
					{buttonsContainer()}
				</Col>
			</Row>
		</div>
	);

	return (
		<BannerWithImage className={styles.resultsView}>
			<div className={styles.resultsWrapper}>
				<div>
					<h4>{I18nt('botiquin_magisterial')}</h4>
					<Info
						className='mb-16'
						show={venceAhora}
						icon={<ExclamationCircleFilled className={styles.infoIcon} />}
						title='tu_receta_vence_hoy'
						body='si_haces_una_reserva_debes'
					/>
					{mobileView()}
					{desktopView()}
					{schedule()}
				</div>
				<div className={styles.botiquinCardsContainer}>
					{botiquinCards()}
					<div className={styles.actionButtonsContainer}>{actionButtons()}</div>
				</div>
			</div>
		</BannerWithImage>
	);
}
